import * as styles from "./TruncateMiddle.css";

import { clsx } from "clsx";
import { type JSX, useRef } from "react";

import type { TruncateMiddleProps } from "./TruncateMiddle.types";
import { DEFAULT_MIN_CHARS } from "./constants";
import { useTruncateMiddleLayout } from "./useTruncateMiddleLayout";

export function TruncateMiddle({
  text,
  minChars = DEFAULT_MIN_CHARS,
  className,
}: TruncateMiddleProps): JSX.Element {
  const splitIndex = Math.ceil(text.length / 2);
  const start = text.slice(0, splitIndex);
  const end = text.slice(splitIndex);
  const floorStart = start.slice(0, minChars);
  const floorEnd = end.slice(-minChars);

  const rootRef = useRef<HTMLSpanElement>(null);
  const startRef = useRef<HTMLSpanElement>(null);
  const endRef = useRef<HTMLSpanElement>(null);
  const floorStartRef = useRef<HTMLSpanElement>(null);
  const floorEndRef = useRef<HTMLSpanElement>(null);

  const { startWidth, endWidth, minWidth } = useTruncateMiddleLayout(
    rootRef,
    startRef,
    endRef,
    floorStartRef,
    floorEndRef,
    text,
    minChars,
  );

  return (
    <span
      ref={rootRef}
      data-slot="truncate-middle"
      title={text}
      className={clsx(styles.root, className)}
      style={{ minWidth }}
    >
      <span
        ref={startRef}
        className={styles.start}
        style={{ width: startWidth }}
      >
        {start}
      </span>
      <span ref={endRef} className={styles.end} style={{ width: endWidth }}>
        {end}
      </span>
      <span ref={floorStartRef} className={clsx(styles.start, styles.probe)}>
        {floorStart}
      </span>
      <span ref={floorEndRef} className={clsx(styles.end, styles.probe)}>
        {floorEnd}
      </span>
    </span>
  );
}
